// path: frontend/src/lib/plan-limits.ts

/**
 * Monthly video quotas per subscription tier (UI helper).
 *
 * IMPORTANT:
 * - The backend (or Firebase callable) is authoritative for `used`
 *   and typically for `limit`.
 * - These plan limits act as UI defaults/fallbacks when the backend
 *   does not specify a limit.
 *
 * Updated Pricing (October 2025):
 *   free      – 10 videos/mo ($0)
 *   starter   – 30 videos/mo ($9.99)
 *   pro       – 100 videos/mo ($29.99)
 *
 * Override at build time with:
 *   VITE_PLAN_LIMITS='{"free":10,"starter":30,"pro":100}'
 *
 * Notes:
 * - We use `-1` as the "unlimited" sentinel.
 */

/** Format a numeric monthly limit for display (no units, no ∞). */
export function formatLimit(limit: number): string {
  // Clamp to non-negative integer
  const safe = Math.max(0, limit | 0);
  return new Intl.NumberFormat("en-US").format(safe);
}

export type Plan = "free" | "starter" | "pro";
export const UNLIMITED = -1 as const;

type LimitsMap = Record<Plan, number>;

const DEFAULT_LIMITS: LimitsMap = {
  free: 10,
  starter: 30,
  pro: 100,
};

function loadLimits(): LimitsMap {
  const raw = (import.meta as any)?.env?.VITE_PLAN_LIMITS as string | undefined;
  if (!raw) return DEFAULT_LIMITS;

  try {
    const parsed = JSON.parse(raw) as Partial<Record<Plan, unknown>>;
    const merged: LimitsMap = { ...DEFAULT_LIMITS };

    (Object.keys(DEFAULT_LIMITS) as Plan[]).forEach((plan) => {
      const v = Number(parsed[plan]);

      // Allow explicit UNLIMITED sentinel (-1)
      if (v === UNLIMITED) {
        merged[plan] = UNLIMITED;
        return;
      }

      // Finite, non-negative integer → accept; otherwise fall back
      if (Number.isFinite(v) && v >= 0) {
        merged[plan] = Math.floor(v);
      } else {
        merged[plan] = DEFAULT_LIMITS[plan];
      }
    });

    return merged;
  } catch {
    // On parse error, fall back completely
    return DEFAULT_LIMITS;
  }
}

/** Final limits used by the UI (possibly overridden by env). */
export const PLAN_LIMITS: LimitsMap = loadLimits();

/** True if the plan has unlimited quota (UI sentinel). */
export function isUnlimited(plan: Plan): boolean {
  return PLAN_LIMITS[plan] === UNLIMITED;
}

/** Numeric monthly limit for a plan (UNLIMITED = -1). */
export function limitFor(plan: Plan): number {
  return PLAN_LIMITS[plan];
}

/** Remaining quota (Infinity for unlimited). */
export function remainingFor(used: number, plan: Plan): number {
  const limit = PLAN_LIMITS[plan];
  if (limit === UNLIMITED) return Infinity;
  const safeUsed = Math.max(0, used | 0);
  return Math.max(0, limit - safeUsed);
}

/** Can the user create `count` more items under this plan? */
export function canCreate(used: number, plan: Plan, count = 1): boolean {
  const limit = PLAN_LIMITS[plan];
  if (limit === UNLIMITED) return true;
  const safeUsed = Math.max(0, used | 0);
  const safeCount = Math.max(1, count | 0);
  return safeUsed + safeCount <= limit;
}

/** "X / Y" string for badges. Unlimited returns "∞". */
export function formatRemaining(used: number, plan: Plan): string {
  const limit = PLAN_LIMITS[plan];
  if (limit === UNLIMITED) return "∞";
  return `${remainingFor(used, plan)} / ${limit}`;
}

/**
 * Extract a Plan from auth claims (best-effort).
 * Looks at `claims.plan`, `claims.subscription`, or defaults to "free".
 */
export function planFromClaims(claims?: any): Plan {
  const raw =
    (claims?.plan ??
      claims?.subscription ??
      claims?.tier ??
      "free") as string;

  const p = String(raw).toLowerCase();
  const known: Plan[] = ["free", "starter", "pro"];
  return (known.includes(p as Plan) ? p : "free") as Plan;
}

/* ──────────────────────────────────────────────────────────────
   Backend-aware helpers (prefer server limit when provided)
   ────────────────────────────────────────────────────────────── */

/** Is a numeric limit the unlimited sentinel? */
export function isUnlimitedLimit(
  limit: number | undefined | null,
): boolean {
  return limit === UNLIMITED;
}

/**
 * Choose the effective limit:
 * - If backendLimit is provided, use it (supports -1 for unlimited).
 * - Else, fall back to plan-based limit.
 */
export function effectiveLimit(opts: {
  backendLimit?: number | null;
  plan?: Plan;
}): number {
  const { backendLimit, plan = "free" } = opts;
  if (typeof backendLimit === "number") {
    return backendLimit;
  }
  return limitFor(plan);
}

/** Remaining using the effective (backend-preferred) limit. */
export function effectiveRemaining(
  used: number,
  opts: { backendLimit?: number | null; plan?: Plan },
): number {
  const limit = effectiveLimit(opts);
  if (limit === UNLIMITED) return Infinity;
  const safeUsed = Math.max(0, used | 0);
  return Math.max(0, limit - safeUsed);
}

/** Can create N more with effective (backend-preferred) limit. */
export function canCreateWithEffectiveLimit(
  used: number,
  opts: { backendLimit?: number | null; plan?: Plan },
  count = 1,
): boolean {
  const limit = effectiveLimit(opts);
  if (limit === UNLIMITED) return true;
  const safeUsed = Math.max(0, used | 0);
  const safeCount = Math.max(1, count | 0);
  return safeUsed + safeCount <= limit;
}
